import { useMemo } from 'react'
import { Style } from '@mui/icons-material'
import { IconButton, Menu, MenuItem } from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'
import { useProjectContext } from '../project-tables/ProjectProvider'

export type OptionsKeyType = keyof typeof options

const options: Record<string, string> = {
  entrataVendorCard: 'Vendor Select',
  entrataPropertyCard: 'Property Select',
  vendorCard: 'Vendor Select',
  flags: 'Flags',
  fields: 'Fields',
}

export const ENTRATA_ORG_ID = '018b8be7-6182-4beb-24a1-4819669451c7'

export const RECEIPT_VENDOR_INFO_TYPE_ID =
  '0192d9d9-e8f9-d21a-3e31-8c795804cb46'
export const INVOICE_VENDOR_INFO_TYPE_ID =
  '0197c775-0c7b-d58e-0a2b-f1f29471c106'

export function useGetVisibleCardsState() {
  const { rootOrganization } = useRootOrganization()
  const { selectedWorkflowState } = useSelectedWorkflowStateContext()

  const defaultVisibleCards = useMemo(() => {
    const visibleCards: OptionsKeyType[] = ['fields']
    if (rootOrganization?.id === ENTRATA_ORG_ID) {
      if (selectedWorkflowState?.name === 'Property Match') {
        visibleCards.unshift('entrataPropertyCard')
      } else if (selectedWorkflowState?.name === 'Vendor Match') {
        visibleCards.unshift('entrataVendorCard')
      }
    }
    if (selectedWorkflowState?.name === 'Match Vendor') {
      visibleCards.unshift('vendorCard')
    }
    return visibleCards
  }, [rootOrganization?.id, selectedWorkflowState?.name])

  return useLocalStorage<OptionsKeyType[]>(
    `validation-visible-cards-${selectedWorkflowState?.id}`,
    defaultVisibleCards,
  )
}

export default function VisibleCardsControl() {
  const { rootOrganization } = useRootOrganization()
  const overlay = useOverlay()

  const [visibleCards, setVisibleCards] = useGetVisibleCardsState()

  const { fieldsMap } = useProjectContext()

  const showVendorSelect = useMemo(() => {
    return Object.values(fieldsMap).some(
      (field) =>
        field.info_type_id === INVOICE_VENDOR_INFO_TYPE_ID ||
        field.info_type_id === RECEIPT_VENDOR_INFO_TYPE_ID,
    )
  }, [fieldsMap])

  if (!rootOrganization) {
    return null
  }

  return (
    <>
      <IconButton size="small" onClick={overlay.open}>
        <Style />
      </IconButton>
      {overlay.anchorEl && (
        <Menu
          open={overlay.isOpen}
          onClose={overlay.close}
          anchorEl={overlay.anchorEl as HTMLButtonElement}
          elevation={16}
        >
          {Object.entries(options).map(([key, value]) => {
            if (
              ['entrataVendorCard', 'entrataPropertyCard'].includes(key) &&
              rootOrganization.id !== ENTRATA_ORG_ID
            ) {
              return null
            }

            // We only show the vendor select if we have a vendor field
            if (key === 'vendorCard' && !showVendorSelect) {
              return null
            }

            return (
              <MenuItem
                dense
                key={key}
                onClick={() => {
                  setVisibleCards((prev) => {
                    if (prev.includes(key as OptionsKeyType)) {
                      return prev.filter((item) => item !== key)
                    }
                    return [...prev, key] as OptionsKeyType[]
                  })
                }}
                selected={visibleCards.includes(key as OptionsKeyType)}
              >
                {value}
              </MenuItem>
            )
          })}
        </Menu>
      )}
    </>
  )
}
